// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Renders assistant output. The model writes light markdown; this turns it into typography.
 *
 * Patrick, 2026-08-23: the reply was rendered with white-space: pre-wrap, so `**17 tickets**`
 * appeared literally, asterisks and all — "very unprofessional". Correct, and the asterisks were
 * never meant to be read; they were markup nobody was interpreting.
 *
 * WHY NOT A MARKDOWN LIBRARY. Pulling marked + DOMPurify into a HIPAA product to format one panel
 * means a parser, a sanitiser and their transitive tree, all rendering model output through
 * dangerouslySetInnerHTML. This builds REACT NODES instead — there is no HTML string anywhere in
 * this file, so there is no injection surface to sanitise. The subset covers what the assistant
 * actually emits: bold, inline code, bullets, numbered lists and headings.
 *
 * Anything outside that subset renders as its own literal text, which is the safe failure: an
 * unhandled construct looks slightly plain, never broken and never executable.
 */

/** Split one line into text / **bold** / `code` React nodes. */
function inline(line: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  // Alternating split on **bold** and `code`. Both are non-nesting in practice, so one pass over
  // a combined pattern is enough and avoids a second escaping problem.
  const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
  parts.forEach((p, i) => {
    if (!p) return
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      out.push(<strong key={`${keyPrefix}-b${i}`}>{p.slice(2, -2)}</strong>)
    } else if (p.startsWith('`') && p.endsWith('`') && p.length > 2) {
      out.push(
        <code key={`${keyPrefix}-c${i}`}
              style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                       borderRadius: 4, padding: '0 4px', fontSize: '0.92em' }}>
          {p.slice(1, -1)}
        </code>
      )
    } else {
      out.push(<span key={`${keyPrefix}-t${i}`}>{p}</span>)
    }
  })
  return out
}

export default function AssistantText({ text }: { text: string }) {
  const lines = text.split('\n')
  const blocks: React.ReactNode[] = []
  let bullets: React.ReactNode[] = []
  let ordered: React.ReactNode[] = []

  const flush = () => {
    if (bullets.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} style={listStyle}>{bullets}</ul>)
      bullets = []
    }
    if (ordered.length) {
      blocks.push(<ol key={`ol-${blocks.length}`} style={listStyle}>{ordered}</ol>)
      ordered = []
    }
  }

  lines.forEach((raw, i) => {
    const line = raw.trimEnd()
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line)

    if (bullet) {
      if (ordered.length) flush()
      bullets.push(<li key={`li-${i}`} style={{ marginBottom: 2 }}>{inline(bullet[1], `l${i}`)}</li>)
      return
    }
    if (numbered) {
      if (bullets.length) flush()
      ordered.push(<li key={`li-${i}`} style={{ marginBottom: 2 }}>{inline(numbered[1], `l${i}`)}</li>)
      return
    }
    flush()
    if (heading) {
      blocks.push(
        <div key={`h-${i}`} style={{ fontWeight: 700, marginTop: blocks.length ? 8 : 0, marginBottom: 2 }}>
          {inline(heading[1], `l${i}`)}
        </div>
      )
      return
    }
    if (line.trim() === '') {
      // Preserve the author's paragraph breaks, but not a run of them.
      if (blocks.length && blocks[blocks.length - 1] !== SPACER) blocks.push(SPACER)
      return
    }
    blocks.push(<div key={`p-${i}`} style={{ marginBottom: 2 }}>{inline(line, `l${i}`)}</div>)
  })
  flush()

  return <div>{blocks}</div>
}

const SPACER = <div key="spacer" style={{ height: 7 }} />
const listStyle: React.CSSProperties = { margin: '2px 0 6px', paddingLeft: 20 }
